/* src/pages/MDPage.tsx */
import React, { useState } from 'react'
import { Mail, Phone, Linkedin, ChevronUp, Facebook } from 'lucide-react'
import { InstagramLogoIcon } from '@radix-ui/react-icons'
import heroImg from './Images/MDMobile/image.png'
import loginIcon from './Images/MDMobile/Login.png'
import elipse from './Images/MDMobile/Ellipse.svg'
import MDLogo from './Images/MDMobile/MDLogo.png'

/* --- Components auxiliares ------------------------------------------------ */
const ServiceCard: React.FC<{ title: string }> = ({ title }) => (
  <div className="rounded-lg bg-white backdrop-blur-sm p-6 text-center text-xs font-semibold text-black">{title}</div>
)

const TextInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className="mt-1 h-8 w-full rounded-2xl bg-white px-3 text-black placeholder-white/70 focus:outline-none"
  />
)

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', tel: '', email: '', message: '' })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Form submitted:', formData)
    setFormData({ name: '', tel: '', email: '', message: '' })
    alert('Mensagem enviada com sucesso!')
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-8 w-full max-w-md rounded-3xl bg-[#828282] px-8 py-10 text-xs text-white backdrop-blur"
    >
      <div className="flex gap-4">
        <div className="flex-1">
          <label htmlFor="name">Nome*</label>
          <TextInput id="name" name="name" required value={formData.name} onChange={handleChange} />
        </div>
        <div className="flex-1">
          <label htmlFor="tel">Telefone*</label>
          <TextInput id="tel" name="tel" type="tel" required value={formData.tel} onChange={handleChange} />
        </div>
      </div>

      <label htmlFor="email" className="mt-4 block">
        Email*
      </label>
      <TextInput id="email" name="email" type="email" required value={formData.email} onChange={handleChange} />

      <label htmlFor="message" className="mt-4 block">
        Mensagem*
      </label>
      <textarea
        id="message"
        name="message"
        required
        value={formData.message}
        onChange={handleChange}
        className="mt-1 h-24 w-full resize-none rounded-2xl bg-white p-3 text-black placeholder-white/70 focus:outline-none"
      />

      <button
        type="submit"
        className="mt-6 w-full rounded-xl bg-orange-600 py-2 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-orange-700"
      >
        Enviar
      </button>
    </form>
  )
}

/* --- Página ---------------------------------------------------------------- */
const MDPage: React.FC = () => (
  <main className="min-h-screen w-full overflow-x-hidden bg-gray-100">
    {/* Wrapper opcional p/ limitar largura em desktops enormes */}

    {/* === HERO =========================================================== */}
    <section className="relative flex min-h-[60vh] flex-col justify-between px-4 py-8 text-white">
      <img
        src={heroImg}
        alt="Plano de fundo hero"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between">
        <img src={MDLogo} alt="MD Mobile logo" className="h-14 w-auto" />
        <nav className="flex gap-2 text-[11px] font-semibold">
          {['Seja um Parceiro', 'Contato', 'Serviços'].map(label => (
            <button
              key={label}
              className="rounded-xl border border-white/70 bg-white/20 px-3 py-1 backdrop-blur hover:bg-white/30"
            >
              {label}
            </button>
          ))}
          <button aria-label="Menu">
            <img src={loginIcon} alt="Menu" className="h-7 w-7" />
          </button>
        </nav>
      </header>

      {/* Chamada principal */}
      <div className="relative z-10 flex w-40 h-72 md:w-[350px] md:h-[280px] flex-col items-center justify-center text-center md:absolute md:-left-5 md:top-1/2 md:-translate-y-1/2">
        <img src={elipse} alt="" className="absolute inset-0 w-full h-full object-contain z-0" />

        <div className="relative z-10 px-4">
          <h1 className="text-lg font-extrabold leading-tight text-white">
            Quer entender a
            <br />
            tributação da sua empresa?
          </h1>
          <p className="mt-2 text-[11px] font-medium text-white">
            Conte conosco para esclarecer e organizar cada detalhe e garantir uma gestão tributária segura!
          </p>
          <button className="mt-4 rounded-xl bg-orange-600 px-4 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-orange-700">
            Entre em contato
          </button>
        </div>
      </div>
    </section>

    {/* === ÁREAS DE ATUAÇÃO ============================================== */}
    <section className="bg-[#828282] px-6 py-12 text-center text-white">
      <h2 className="text-sm font-bold">Áreas de Atuação</h2>
      <div className="mt-6 grid grid-cols-2 gap-2 md:grid-cols-3">
        {[
          'Assessoria Tributária',
          'Assessoria Jurídica Tributária',
          'Consultoria',
          'Licitações',
          'Atendimento Personalizado',
          'Contabilidade'
        ].map(title => (
          <ServiceCard key={title} title={title} />
        ))}
      </div>
    </section>

    {/* === QUEM SOMOS ===================================================== */}
    <section className="bg-[#EFEEEE] px-6 py-16 text-[11px] leading-relaxed text-black">
      <h2 className="text-xs font-bold text-[#0C224F]">Quem Somos?</h2>
      <p className="mt-6">
        Na <strong>MD Tributaria</strong>, simplificamos o universo da tributação para que você possa focar no que
        realmente importa: fazer sua empresa crescer de forma sustentável.
        <br />
        <br />
        Com muita experiência e uma equipe altamente qualificada, oferecemos soluções personalizadas que otimizam seus
        processos fiscais, asseguram conformidade legal e eliminam surpresas desagradáveis.
        <br />
        <br />
        <span className="font-semibold">
          Tem dúvidas ou quer saber mais? Entre em contato conosco agora mesmo. Estamos prontos para ajudar você a
          alcançar seus objetivos com tranquilidade e confiança.
        </span>
      </p>
    </section>

    {/* === CONTATO ======================================================== */}
    <section className="flex flex-col items-center px-4 py-16 bg-inherit bg-white ">
      <h2 className="text-sm font-bold text-black">Contato</h2>

      <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12 rounded-2xl bg-[#828282] px-6 py-4 text-xs text-white backdrop-blur w-full max-w-md">
        <a href="mailto:contato@mdtributaria.com.br" className="flex items-center gap-2">
          <Mail className="h-4 w-4" /> contato@mdtributaria.com.br
        </a>
        <a href="tel:+553100000000" className="flex items-center gap-2">
          <Phone className="h-4 w-4" /> (31) 0000-0000
        </a>
      </div>
      <p className="mt-6 text-center text-xs text-black">
        De segunda à sexta-feira das 09h00 às 18h00
        <br />
        ou entre em contato através do formulário abaixo.
      </p>
      <ContactForm />
    </section>

    {/* === FOOTER ========================================================= */}
    <footer className="bg-[#828282] text-white w-full flex flex-col items-center px-6 py-10 mt-20">
      <h3 className="text-white text-sm font-extrabold">SIGA-NOS</h3>

      <div className="flex flex-col md:flex-row items-center justify-center    gap-6 w-full max-w-4xl mt-6 text-white  text-xs font-medium text-center md:text-left">
        <div>
          Acompanhe a MD Tributaria nas
          <br /> redes sociais
        </div>

        <div className="flex items-center gap-4">
          <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
            <Facebook className="w-8 h-8 hover:opacity-80 transition-opacity" />
          </a>
          <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
            <InstagramLogoIcon className="w-8 h-8 hover:opacity-80 transition-opacity" />
          </a>
          <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
            <Linkedin className="w-8 h-8 hover:opacity-80 transition-opacity" />
          </a>
        </div>
      </div>
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50  text-black p-3 rounded-full shadow-lg  transition-colors"
        aria-label="Voltar ao topo"
      >
        <ChevronUp className="w-5 h-5" />
      </button>
      <hr className="border-white  w-full max-w-4xl my-8" />

      <div className="flex  md:flex-row justify-between items-start gap-8 w-full max-w-4xl text-white text-xs font-normal">
        <div className="flex items-start gap-4">
          <img src={MDLogo} alt="Bahia Sul Logo" className="w-12 object-contain" />
          <nav className="flex flex-col space-y-1">
            <a href="#" className="hover:underline">
              Home
            </a>
            <a href="#contact" className="hover:underline">
              Contato
            </a>
            <a href="#services" className="hover:underline">
              Serviços
            </a>
          </nav>
        </div>

        <address className="not-italic leading-relaxed">
          <div className="font-bold mb-1">Endereço</div>
          Rua xx
          <br />
          Belo Horizonte - MG
          <br />
          CEP: 31310-000, Brasil
        </address>
      </div>
    </footer>
  </main>
)

export default MDPage
